import {
  CSSProperties,
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { TextOrQuillText } from '@betterfunnel/types';
import { Delta, Parchment } from 'quill/core';
import { useDebouncedCallback } from '../hooks/useDebouncedCallback';
import { classNames } from '../utils';
import ReactQuill from './ReactQuill';

import Quill from 'quill';
import { Mention, MentionBlot, MentionEvent } from 'quill-mention';
import { uid } from 'uid';
import EditQuillSearchParamMentionModal from '../modals/EditQuillSearchParamMentionModal';
import CustomToolbarPopup from './CustomToolbarPopup';

const { ClassAttributor } = Parchment;

const SelectionBackground = new ClassAttributor(
  'selectionBackground', // format name
  'ql-selection-background', // CSS class prefix
  { scope: Parchment.Scope.INLINE },
);

Quill.register(SelectionBackground, true);

Quill.register({ 'blots/mention': MentionBlot, 'modules/mention': Mention });

const _MentionBlot: any = Quill.import('blots/mention');

class CustomValueMentionBlot extends _MentionBlot {
  static render(data: any) {
    const element = document.createElement('span');

    element.innerText = data.value;
    // element.style.color = data.color || '#302CF4';

    const endMention = document.createElement('span');
    endMention.className = 'ql-mention-denotation-char';

    endMention.innerText = '}}';

    element.appendChild(endMention);

    return element;
  }
}

CustomValueMentionBlot.blotName = 'custom-value-mention';
CustomValueMentionBlot.className = 'custom-value-mention';

Quill.register(CustomValueMentionBlot);

type CustomValueMentionTypeBase = {
  id: string;
  type: string;
  value: string;
  description?: string;
};
type DateCustomValueMentionType = CustomValueMentionTypeBase & {
  type: 'date';
  dateFormat?: string;
};
type KeywordCustomValueMentionType = CustomValueMentionTypeBase & {
  type: 'keyword';
};
type SearchParamCustomValueMentionType = CustomValueMentionTypeBase & {
  type: 'search-param';
  searchParam?: string;
  defaultValue?: string;
};
type CustomValueMentionType =
  | DateCustomValueMentionType
  | KeywordCustomValueMentionType
  | SearchParamCustomValueMentionType;

const customValueMentionOptions: CustomValueMentionType[] = [
  {
    id: 'keyword',
    type: 'search-param',
    value: 'Keyword',
    description: 'Embed the keyword url param in the text',
    searchParam: 'keyword',
    defaultValue: '',
  },
  {
    id: 'search-param',
    type: 'search-param',
    value: 'Search Param',
    description: 'Embed a search param in the text',
  },
  {
    id: 'date:normal',
    type: 'date',
    value: 'MM/DD/YYYY',
    dateFormat: 'MM/DD/YYYY',
    description: 'Embed the current date in the text. Format: MM/DD/YYYY',
  },
  {
    id: 'date:month',
    type: 'date',
    value: 'Month',
    dateFormat: 'MM',
    description: 'Embed the current month in the text. Format: MM',
  },
  {
    id: 'date:day',
    type: 'date',
    value: 'Day',
    dateFormat: 'DD',
    description: 'Embed the current day in the text. Format: DD.',
  },
  {
    id: 'date:year',
    type: 'date',
    value: 'Year',
    dateFormat: 'YYYY',
    description: 'Embed the current year in the text. Format: YYYY.',
  },
  {
    id: 'date:day-of-week',
    type: 'date',
    value: 'Day of week',
    dateFormat: 'dddd',
    description: 'Embed the name of the current day of the week in the text.',
  },
  {
    id: 'date:day-of-week-short',
    type: 'date',
    value: 'Day of week short',
    dateFormat: 'ddd',
    description:
      'Embed the short name of the current day of the week in the text.',
  },
  {
    id: 'date:month-name',
    type: 'date',
    value: 'Month name',
    dateFormat: 'MMMM',
    description: 'Embed the name of the current month in the text.',
  },
  {
    id: 'date:month-name',
    type: 'date',
    value: 'Month short name',
    dateFormat: 'MMM',
    description: 'Embed the short name of the current month in the text.',
  },
];

const Size: any = Quill.import('attributors/style/size');
const quilSizes = [
  '4px',
  '6px',
  '8px',
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '24px',
  '28px',
  '32px',
  '36px',
  '40px',
  '48px',
  '56px',
  '64px',
  '72px',
  '80px',
  '96px',
];
Size.whitelist = quilSizes;
Quill.register(Size, true);

const FontWeightStyle = new Parchment.StyleAttributor(
  'fontweight',
  'font-weight',
  {
    scope: Parchment.Scope.INLINE,
    whitelist: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
  },
);
Quill.register(FontWeightStyle, true);

export const quillColors = [
  '',
  'var(--color-primary)',
  '#000000', // Black
  '#FFFFFF', // White
  '#374151', // Gray 600
  '#1f2937', // Gray 900
  '#e60000', // Red
  '#ff9900', // Orange
  '#ffff00', // Yellow
  '#008a00', // Green
  '#0066cc', // Blue
  '#9933ff', // Purple
  '#facccc', // Light Red
  '#ffebcc', // Light Orange
  '#ffffcc', // Light Yellow
  '#cce8cc', // Light Green
  '#cce0f5', // Light Blue
  '#ebd6ff', // Light Purple
  '#bbbbbb', // Light Gray
  '#f06666', // Dark Red
  '#ffc266', // Dark Orange
  '#ffff66', // Dark Yellow
  '#66b966', // Dark Green
  '#66a3e0', // Dark Blue
  '#c285ff', // Dark Purple
  '#888888', // Medium Gray
  '#a10000', // Darker Red
  '#b26b00', // Darker Orange
  '#b2b200', // Darker Yellow
  '#006100', // Darker Green
  '#0047b2', // Darker Blue
  '#6b24b2', // Darker Purple
  '#444444', // Dark Gray
  '#5c0000', // Very Dark Red
  '#663d00', // Very Dark Orange
  '#666600', // Very Dark Yellow
  '#003700', // Very Dark Green
  '#002966', // Very Dark Blue
  '#3d1466', // Very Dark Purple
];

interface Props {
  title?: string;
  titlePosition?: 'default' | 'overlapping';

  placeholder?: string;
  // input?: InputProps;
  value?: TextOrQuillText;
  readOnly?: boolean;
  className?: string;
  onChange?: (d: Delta) => void;
  validation?: {
    status?: 'error' | 'success';
    text?: string;
  };
  style?: CSSProperties;
  startIcon?: ReactElement;
  endIcon?: ReactElement;
  fullWidth?: boolean;
  onClick?: MouseEventHandler<HTMLInputElement>;
  noDesign?: boolean;
  debounce?: boolean;
  big?: boolean;
  trailing?: ReactElement;
  generateSuggestions?: (
    textSelection: Delta,
    fullLine: Delta,
    prompt: string,
  ) => Promise<any>;
}

export default function QuillField({
  title,
  placeholder,
  titlePosition = 'default',
  // input,
  value,
  readOnly,
  className,
  onChange,
  validation,
  style,
  startIcon,
  endIcon,
  fullWidth,
  noDesign,
  debounce,
  big,
  trailing,
  generateSuggestions,
}: Props) {
  const [quillInstance, setQuillInstance] = useState<Quill | null>(null);
  const [showSearchParamMentionModal, setShowSearchParamMentionModal] =
    useState<{ mention: any }>();
  const debouncedOnChange = useDebouncedCallback(
    onChange ?? (() => {}),
    debounce ? 1000 : 0,
  );

  const onMentionAdded = useCallback((item: any) => {
    if (!('type' in item) || item.type !== 'search-param') {
      return;
    }
    setShowSearchParamMentionModal({ mention: item });
  }, []);

  useEffect(() => {
    const onMentionClicked = (event: MentionEvent) => {
      if (
        !quillInstance ||
        !event?.event?.target ||
        !(event.event.target instanceof Node) ||
        !quillInstance.root.contains(event.event.target)
      ) {
        return;
      }

      if ('type' in event.value && event.value?.type === 'search-param') {
        setShowSearchParamMentionModal({ mention: event.value });
        return;
      }
    };

    window.addEventListener('mention-clicked', onMentionClicked as any, false);

    return () => {
      window.removeEventListener('mention-clicked', onMentionClicked as any);
    };
  }, [quillInstance]);

  const modules = useMemo(() => {
    return {
      mention: {
        allowedChars: /^[A-Za-zÅÄÖåäö]*$/,
        mentionDenotationChars: ['{{'],
        positioningStrategy: 'fixed',
        mentionContainerClass:
          'ql-mention-list-container ql-mention-list-container-top w-72 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in overflow-scroll',
        mentionListClass: 'ql-mention-list divide-y divide-gray-200',
        listItemClass: 'ql-mention-list-item group',
        renderLoading: () => {
          return 'Loading...';
        },
        renderItem: (data: any) => {
          const ListboxOption = document.createElement('div');
          ListboxOption.className =
            'cursor-pointer select-none p-4 text-sm text-gray-900 group-hover:bg-indigo-600 group-hover:text-white group-[.selected]:bg-indigo-600 group-[.selected]:text-white';

          const flexContainer = document.createElement('div');
          flexContainer.className = 'flex flex-col';

          const flexJustify = document.createElement('div');
          flexJustify.className = 'flex justify-between';

          const title = document.createElement('p');
          title.className = 'font-normal';
          title.innerText = data.value;

          flexJustify.appendChild(title);

          flexContainer.appendChild(flexJustify);

          if (data.description) {
            const description = document.createElement('p');
            description.className =
              'mt-2 text-gray-500 group-[.selected]:text-indigo-200 group-hover:text-indigo-200';
            description.innerText = data.description;

            flexContainer.appendChild(description);
          }

          ListboxOption.appendChild(flexContainer);

          return ListboxOption;
        },
        source: async function (searchTerm: string, renderList: any) {
          const list = customValueMentionOptions;
          if (searchTerm) {
            renderList(
              list.filter((item) =>
                item.value.toLowerCase().startsWith(searchTerm.toLowerCase()),
              ),
            );
          } else {
            renderList(list);
          }
        },
        dataAttributes: [
          'id',
          'value',
          'type',
          'dateFormat',
          'denotationChar',
          'link',
          'target',
          'disabled',
          'color',
          'searchParam',
        ],
        onSelect: (item: any, insertItem: any) => {
          const newItem = { ...item, id: uid(16) };
          insertItem(newItem);

          onMentionAdded(newItem);
        },
        blotName: 'custom-value-mention',
      },
    };
  }, [onMentionAdded]);

  return (
    <div
      className={classNames(
        'text-editor relative',
        big ? 'big-quill' : '',
        fullWidth ? 'w-full' : '',
      )}
    >
      <EditQuillSearchParamMentionModal
        mention={showSearchParamMentionModal?.mention}
        open={!!showSearchParamMentionModal}
        onClose={() => {
          setShowSearchParamMentionModal(undefined);
        }}
        onSave={(newMention) => {
          const quill = quillInstance;
          if (!quill) return;

          quill.setContents(
            quill.getContents().ops.map((op) => {
              const insert = op.insert as any;
              const currentMention = insert?.['custom-value-mention'];
              if (currentMention && currentMention.id === newMention.id) {
                return {
                  ...op,
                  insert: {
                    ...insert,
                    'custom-value-mention': newMention,
                  },
                };
              }

              return op;
            }),
            'user',
          );

          setShowSearchParamMentionModal(undefined);
        }}
      />
      {title ? (
        <div className="relative flex flex-row gap-1">
          <label
            htmlFor="email"
            className={classNames(
              titlePosition === 'overlapping'
                ? 'absolute -top-2 left-2 z-10 inline-block bg-white px-1 text-xs font-medium text-gray-900'
                : 'block text-sm font-medium leading-6 text-gray-900',
            )}
          >
            {title}
          </label>
          {trailing}
        </div>
      ) : (
        <></>
      )}
      <div className="relative">
        {startIcon ? (
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center ps-3">
            {startIcon}
          </div>
        ) : (
          <></>
        )}

        <ReactQuill
          theme="bubble"
          defaultValue={value}
          className={classNames(
            'relative block w-full py-1.5 text-gray-900 placeholder:text-gray-400 sm:text-sm sm:leading-6',
            !noDesign &&
              'rounded-md border-0 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600',
            title && titlePosition !== 'overlapping' ? 'mt-2' : '',
            validation?.status === 'success' ? 'ring-green-300' : '',
            validation?.status === 'error' ? 'ring-red-300' : '',
            className,
            startIcon ? 'ps-10' : '',
            endIcon ? 'pe-10' : '',
          )}
          modules={modules}
          onQuillReady={(quill) => setQuillInstance(quill)}
          onChange={(_, __, source, editor) => {
            if (source === 'api') return;

            debouncedOnChange?.(
              JSON.parse(JSON.stringify(editor.getContents())),
            );
          }}
          placeholder={placeholder}
          readOnly={readOnly}
          aria-invalid={validation?.status === 'error' ? 'true' : undefined}
          aria-describedby={validation?.text}
          style={style}
        />
        <CustomToolbarPopup
          quill={quillInstance}
          generateSuggestions={generateSuggestions}
        />
        {endIcon ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pe-3">
            {endIcon}
          </div>
        ) : (
          <></>
        )}
      </div>
      {validation?.text ? (
        <p
          className={classNames(
            'mt-2 text-sm',
            validation?.status === 'error' ? 'text-red-600' : '',
            validation?.status === 'success' ? 'text-green-600' : '',
          )}
          id="email-error"
        >
          {validation.text}
        </p>
      ) : (
        <></>
      )}
    </div>
  );
}
